import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Ship, Lock, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Card, EmptyState, ScanBar } from "./fxui";
import { QrLabelButton } from "./QrLabel";
import { printHtml, manifestHtml } from "./print";
import { Printer } from "lucide-react";

const STATE_TONE = {
  open: "bg-[#D9933B]/15 text-[#B5771F]", sealed: "bg-black/10 text-[#5C5C5C]",
  dispatched: "bg-[#2563EB]/15 text-[#2563EB]", arrived: "bg-[#7C3AED]/15 text-[#7C3AED]",
  received: "bg-[#0891B2]/15 text-[#0891B2]", opened: "bg-[#0891B2]/15 text-[#0891B2]",
  sorted: "bg-[#2D7A5D] text-white",
};

export default function FxContainers() {
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [creating, setCreating] = useState(false);
  const [route, setRoute] = useState("UK to Ghana");
  const [dispatch, setDispatch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/fx/containers"); setContainers(data.containers || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    await api.post("/fx/containers", { route, dispatch_date: dispatch });
    toast.success("Container created"); setCreating(false); setDispatch(""); load();
  };

  return (
    <div data-testid="fx-containers">
      <div className="flex justify-end mb-5">
        <button onClick={() => setCreating(true)} data-testid="fx-container-new"
          className="flex items-center gap-2 bg-[#306D29] hover:bg-[#0D530E] text-white px-4 py-2.5 rounded-full text-sm font-semibold"><Plus size={16} /> New container</button>
      </div>
      {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#306D29]" /></div>
        : containers.length === 0 ? <EmptyState>No containers yet. Create one to start consolidating packages.</EmptyState>
        : (
        <div className="space-y-2">
          {containers.map((c) => (
            <button key={c.container_number} data-testid={`fx-container-row-${c.container_number}`} onClick={() => setActive(c.container_number)}
              className="w-full text-left bg-white border border-black/5 rounded-2xl px-5 py-4 hover:border-[#306D29]/40 flex items-center gap-3" style={{ transition: "border-color 0.15s ease" }}>
              <Ship size={18} className="text-[#306D29]" />
              <div className="flex-1">
                <p className="font-semibold text-[#0D530E]">{c.container_number}</p>
                <p className="text-xs text-[#5C5C5C]">{c.route} · {c.total_orders} orders · {c.total_packages} packages</p>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${STATE_TONE[c.manifest_status] || ""}`}>{c.manifest_status}</span>
            </button>
          ))}
        </div>
      )}
      {creating && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <div className="bg-[#FBF5DD] rounded-3xl p-7 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="fx-container-modal">
            <h3 className="font-display text-2xl text-[#0D530E] mb-4">New container</h3>
            <input value={route} onChange={(e) => setRoute(e.target.value)} placeholder="Route" data-testid="fx-container-route"
              className="w-full bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm mb-3" />
            <input value={dispatch} onChange={(e) => setDispatch(e.target.value)} type="date" data-testid="fx-container-date"
              className="w-full bg-white border border-black/10 rounded-xl px-3 py-2.5 text-sm mb-4" />
            <div className="flex gap-3">
              <button onClick={() => setCreating(false)} className="flex-1 py-2.5 rounded-full border border-black/10 text-sm">Cancel</button>
              <button onClick={create} data-testid="fx-container-create" className="flex-1 py-2.5 rounded-full bg-[#306D29] text-white text-sm font-semibold">Create</button>
            </div>
          </div>
        </div>
      )}
      {active && <ContainerModal containerNumber={active} onClose={() => { setActive(null); load(); }} />}
    </div>
  );
}

function ContainerModal({ containerNumber, onClose }) {
  const [c, setC] = useState(null);
  const load = useCallback(async () => { const { data } = await api.get(`/fx/containers/${containerNumber}`); setC(data); }, [containerNumber]);
  useEffect(() => { load(); }, [load]);

  const scanIn = async (code) => {
    try { const { data } = await api.post(`/fx/containers/${containerNumber}/scan-package`, { package_number: code.trim() });
      toast.success(`Loaded ${data.package_number}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load package"); }
  };
  const seal = async () => { await api.post(`/fx/containers/${containerNumber}/seal`); toast.success("Sealed"); load(); };
  const dispatch = async () => { await api.post(`/fx/containers/${containerNumber}/dispatch`); toast.success("Dispatched — customers notified"); load(); };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div className="bg-[#FBF5DD] w-full max-w-lg h-full overflow-y-auto p-7" onClick={(e) => e.stopPropagation()} data-testid="fx-container-detail">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-display text-3xl text-[#0D530E]">{containerNumber}</h3>
            {c && <p className="text-sm text-[#5C5C5C]">{c.route} · {c.manifest_status}</p>}
          </div>
          <div className="flex items-center gap-2">
            <QrLabelButton code={containerNumber} title={c ? c.route : ""} subtitle={c ? `${c.total_orders} orders` : ""} testid="fx-container-label" label="Label" />
            {c && <button onClick={() => printHtml(`Manifest ${containerNumber}`, manifestHtml(c))} data-testid="fx-container-manifest"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-black/10 text-[#306D29] hover:border-[#306D29]/40"><Printer size={14} /> Manifest</button>}
            <button onClick={onClose}><X size={22} /></button>
          </div>
        </div>
        {!c ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#306D29]" /></div> : (
          <>
            {c.manifest_status === "open" && <ScanBar onSubmit={scanIn} placeholder="Scan a package (PKG-…) into this container" testid="fx-container-scan" />}
            <div className="grid grid-cols-2 gap-3 mb-5">
              <Card><p className="font-display text-2xl text-[#0D530E]">{c.total_orders}</p><p className="text-xs text-[#5C5C5C]">orders</p></Card>
              <Card><p className="font-display text-2xl text-[#0D530E]">{c.total_packages}</p><p className="text-xs text-[#5C5C5C]">packages</p></Card>
            </div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C5C] font-semibold mb-2">Manifest</p>
            <div className="space-y-2 mb-5">
              {(c.orders_detail || []).map((o) => (
                <div key={o.order_number} className="bg-white border border-black/5 rounded-xl px-4 py-2.5 text-sm flex justify-between">
                  <span className="font-medium">{o.order_number}</span>
                  <span className="text-[#5C5C5C]">{o.customer_name} · {o.city} · {o.packages.length} pkg</span>
                </div>
              ))}
              {(!c.orders_detail || c.orders_detail.length === 0) && <p className="text-sm text-[#5C5C5C]">No packages loaded yet.</p>}
            </div>
            <div className="flex gap-3">
              {c.manifest_status === "open" && <button onClick={seal} data-testid="fx-container-seal" className="flex-1 py-2.5 rounded-full border border-black/10 text-sm font-semibold flex items-center justify-center gap-2"><Lock size={15} /> Seal</button>}
              {c.manifest_status === "sealed" && <button onClick={dispatch} data-testid="fx-container-dispatch" className="flex-1 py-2.5 rounded-full bg-[#306D29] text-white text-sm font-semibold flex items-center justify-center gap-2"><Send size={15} /> Dispatch from UK</button>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
